import { CONTAINEX_PATH_FALLBACK, CONTAINEX_PATHS, type ContainexPaths } from '@/util/containex-paths.ts';
import { useMarketQuery } from '@/composables/market.ts';
import { useI18n } from 'vue-i18n';

const BASE_URL = 'https://www.containex.com';
const FALLBACK_LANGUAGE_MAPPING = new Map<string, string>([
    ['be', 'fr'],
    ['ba', 'bs'],
    ['ch', 'de'],
    ['lu', 'fr'],
    ['me', 'hr'],
]);

export interface ContainexPathHelper {
    retrieveWebsiteUrl(): string;
    retrieveTermsOfUseUrl(): string;
    retrievePrivacyPolicyUrl(): string;
    retrieveImprintUrl(): string;
    retrieveInquiryPath(): string;
}

export function useContainexPathHelper(): ContainexPathHelper {
    const { market } = useMarketQuery();
    const { locale } = useI18n();

    function findCorrectContainexPaths(): ContainexPaths {
        const currentMarket = market?.value?.code.toLowerCase();
        if (currentMarket == null) {
            return CONTAINEX_PATH_FALLBACK;
        }

        const marketUrls = CONTAINEX_PATHS.get(currentMarket);
        if (marketUrls == null || marketUrls.length === 0) {
            return CONTAINEX_PATH_FALLBACK;
        }

        if (marketUrls.length === 1 && marketUrls[0] != null) {
            return marketUrls[0];
        }

        const currentLanguage = locale.value.split('-')[0]?.toLowerCase() ?? '';
        const fallbackLanguageForMarket = FALLBACK_LANGUAGE_MAPPING.get(currentMarket) ?? '';

        return (
            marketUrls.find((paths) => paths.lang === currentLanguage) ??
            marketUrls.find((paths) => paths.lang === fallbackLanguageForMarket) ??
            CONTAINEX_PATH_FALLBACK
        );
    }

    return {
        retrieveWebsiteUrl(): string {
            const containexPaths = findCorrectContainexPaths();
            return BASE_URL + containexPaths.urlPrefix;
        },
        retrieveTermsOfUseUrl(): string {
            const containexPaths = findCorrectContainexPaths();
            return BASE_URL + containexPaths.urlPrefix + containexPaths.termsOfUsePath;
        },
        retrievePrivacyPolicyUrl(): string {
            const containexPaths = findCorrectContainexPaths();
            return BASE_URL + containexPaths.urlPrefix + containexPaths.privacyPolicyPath;
        },
        retrieveImprintUrl(): string {
            const containexPaths = findCorrectContainexPaths();
            return BASE_URL + containexPaths.urlPrefix + containexPaths.imprintPath;
        },
        retrieveInquiryPath(): string {
            const containexPaths = findCorrectContainexPaths();
            return BASE_URL + containexPaths.urlPrefix + containexPaths.inquiryPath;
        },
    };
}
